import { initializeApp } from 'firebase/app';
import { getAuth, setPersistence, browserLocalPersistence, browserSessionPersistence, inMemoryPersistence } from 'firebase/auth';
import { getDatabase, ref, get, onValue, push, set } from 'firebase/database';
import { DB_PATHS } from './schema';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const database = getDatabase(app);
export default app;

// Configure persistence
(async () => {
  try {
    await setPersistence(auth, browserLocalPersistence);
  } catch (err) {
    try { await setPersistence(auth, browserSessionPersistence); } 
    catch (err2) {
      try { await setPersistence(auth, inMemoryPersistence); } 
      catch (err3) { console.error('Persistence failed', err3); }
    }
  }
})();

export const testConnection = async () => {
  try {
    const snap = await get(ref(database, '.info/connected'));
    return snap.val();
  } catch { return false; }
};

export const subscribeToThemeSettings = (callback) => {
  return onValue(ref(database, DB_PATHS.ADMIN_THEME_SETTINGS), (snap) => {
    callback(snap.exists() ? snap.val() : null);
  });
};

// Global exact server offset
export let currentServerTimeOffset = 0;
onValue(ref(database, '.info/serverTimeOffset'), (snap) => {
  currentServerTimeOffset = snap.val() || 0;
});

// Returns the true atomic UTC Unix integer Timestamp
export const getSecureAtomicTime = () => {
  return Date.now() + currentServerTimeOffset;
};

// Replaced by getSecureAtomicTime() and absolute unix epochs

// Helper to grab first available node (for backwards compatibility with dirty DB states)
export const getFirstExistingSnapshot = async (paths) => {
  for (const p of paths) {
    try {
      const snap = await get(ref(database, p));
      if (snap.exists()) return snap;
    } catch (e) {}
  }
  return null;
};

// Modernized fetchers pointing to the centralized Schema
export const getPaymentMethods = async () => {
  try {
    const snap = await get(ref(database, DB_PATHS.ADMIN_PAYMENT_METHODS));
    return snap.exists() ? Object.entries(snap.val()).map(([id, method]) => ({ id, ...method })) : [];
  } catch { return []; }
};

export const getSettings = async () => {
  try {
    const snap = await get(ref(database, DB_PATHS.ADMIN_SETTINGS));
    return snap.exists() ? snap.val() : {
      siteName: 'PSH STORE',
      currency: 'USDT',
      commission: 15,
      downloadLink: '',
      universalScriptLink: '',
      downloadItems: []
    };
  } catch {
    return {
      siteName: 'PSH STORE',
      currency: 'USDT',
      commission: 15,
      downloadLink: '',
      universalScriptLink: '',
      downloadItems: []
    };
  }
};

const normalizeCouponListField = (val) => {
  if (Array.isArray(val)) return val.map((s) => String(s || '').trim()).filter(Boolean);
  if (typeof val === 'string') return val.split(',').map((s) => s.trim()).filter(Boolean);
  return [];
};

const normalizeCouponPayload = (code, coupon = {}, overrides = {}) => {
  const allowedPeriods = [
    ...normalizeCouponListField(coupon.period),
    ...normalizeCouponListField(coupon.periods),
    ...normalizeCouponListField(coupon.allowedPeriods),
    ...normalizeCouponListField(coupon.allowedPeriod),
  ];
  const allowedCategories = [
    ...normalizeCouponListField(coupon.category),
    ...normalizeCouponListField(coupon.categories),
    ...normalizeCouponListField(coupon.allowedCategories),
    ...normalizeCouponListField(coupon.allowedCategory),
  ];
  const allowedProductIds = [
    ...normalizeCouponListField(coupon.productId),
    ...normalizeCouponListField(coupon.productIds),
    ...normalizeCouponListField(coupon.allowedProductIds),
    ...normalizeCouponListField(coupon.allowedProducts),
  ];
  const allowedUserIds = [
    ...normalizeCouponListField(coupon.userId),
    ...normalizeCouponListField(coupon.userIds),
    ...normalizeCouponListField(coupon.allowedUserId),
    ...normalizeCouponListField(coupon.allowedUserIds),
    ...normalizeCouponListField(coupon.allowedUsers),
  ];

  return {
    ...coupon,
    ...overrides,
    code: String(code || coupon.code || '').trim().toUpperCase(),
    maxDiscount: Number(coupon.max || coupon.maxDiscount || 0),
    minPurchaseAmount: Number(coupon.minPurchaseAmount || 0),
    allowedPeriods,
    allowedCategories,
    allowedProductIds,
    allowedUserIds,
  };
};

export const getCoupons = async () => {
  try {
    const snap = await get(ref(database, DB_PATHS.COUPONS));
    if (!snap.exists()) return [];

    return Object.entries(snap.val()).map(([code, coupon]) =>
      normalizeCouponPayload(code, coupon)
    );
  } catch { return []; }
};

export const getUserAssignedCoupons = async (uid) => {
  try {
    if (!uid) return [];
    const snap = await get(ref(database, DB_PATHS.userCoupons(uid)));
    if (!snap.exists()) return [];

    return Object.entries(snap.val()).map(([code, coupon]) =>
      normalizeCouponPayload(code, coupon, {
        isUserAssigned: true,
        visibility: 'user_specific'
      })
    );
  } catch {
    return [];
  }
};

export const fetchCouponByCode = async (code, uid = null) => {
  const normalizedCode = String(code || '').trim().toUpperCase();
  if (!normalizedCode) return null;

  try {
    // 1. Try public coupons
    const publicSnap = await get(ref(database, DB_PATHS.coupon(normalizedCode)));
    if (publicSnap.exists()) {
      return normalizeCouponPayload(normalizedCode, publicSnap.val());
    }

    // 2. Try user-assigned coupons if authenticated
    if (uid) {
      const userCouponSnap = await get(ref(database, DB_PATHS.userCoupon(uid, normalizedCode)));
      if (userCouponSnap.exists()) {
        return normalizeCouponPayload(normalizedCode, userCouponSnap.val(), {
          isUserAssigned: true,
          visibility: 'user_specific'
        });
      }
    }

    // 3. Try direct private coupon read
    const privateSnap = await get(ref(database, DB_PATHS.privateCoupon(normalizedCode)));
    if (privateSnap.exists()) {
      return normalizeCouponPayload(normalizedCode, privateSnap.val(), {
        isPrivate: true
      });
    }
  } catch {
    return null;
  }

  return null;
};

export const getUserRewardPoints = async (uid) => {
  try {
    if (!uid) return 0;
    const snap = await get(ref(database, DB_PATHS.userRewardPoints(uid)));
    const value = snap.val();
    return typeof value === 'number' && value >= 0 ? value : 0;
  } catch { return 0; }
};

export const getStats = async () => {
  try {
    const snap = await get(ref(database, DB_PATHS.STATS));
    return snap.exists() ? snap.val() : null;
  } catch { return null; }
};

export const addPointsHistory = async (uid, amount, type, reason) => {
  try {
    const newRef = push(ref(database, DB_PATHS.userPointsHistory(uid)));
    await set(newRef, { amount: Number(amount), type, reason, date: new Date().toISOString() });
    return true;
  } catch { return false; }
};
